import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getToken, getRefreshToken, setToken, clearAuth } from '../utils/auth/tokenStorage';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 seconds
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Only add cache-busting parameter when skipCache is true
    if (config.method?.toLowerCase() === 'get' && config.params?.skipCache === true) {
      // Use a stronger cache-busting technique with both timestamp and random number
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000000);
      config.params._t = `${timestamp}-${random}`;
      
      // Remove the skipCache param to avoid cluttering the URL
      delete config.params.skipCache;
    }
    
    // Special handling for meeting endpoints to ensure coHosts is properly included
    if ((config.url?.includes('/api/meetings') && 
         (config.method?.toLowerCase() === 'post' || config.method?.toLowerCase() === 'put')) && 
         config.data) {
      
      // Log the original request data
      console.log("Original request data:", JSON.stringify(config.data));
      
      // Ensure coHosts is included if it exists
      if (config.data.coHosts) {
        console.log("Found coHosts in request:", JSON.stringify(config.data.coHosts));
        
        // Make sure it's sent as an array, even if empty
        if (!Array.isArray(config.data.coHosts)) {
          config.data.coHosts = [];
          console.log("Fixed coHosts to be an array");
        }
      } else {
        // Add empty coHosts array if missing
        config.data.coHosts = [];
        console.log("Added empty coHosts array to request");
      }
      
      // Log the modified request data
      console.log("Modified request data:", JSON.stringify(config.data));
    }
    
    // Special handling for payment-related endpoints
    if (config.url?.includes('/api/payments/') || config.url?.includes('/api/subscriptions/') || 
        config.url?.includes('/api/plans')) {
      // Add extra logging for payment-related requests
      console.log(`PAYMENT API Request: ${config.method?.toUpperCase()} ${config.url}`, 
        config.data ? { 
          data: {
            ...config.data,
            // Remove sensitive data from logs
            gstDetails: config.data.gstDetails ? 'present' : undefined,
            razorpaySignature: config.data.razorpaySignature ? '[REDACTED]' : undefined
          } 
        } : '');
        
      // For payment endpoints, set a flag in the config object itself rather than in headers
      config.isPaymentRequest = true;
      
      // Increase timeout for payment endpoints
      config.timeout = 30000; // 30 seconds for payment requests
    }
    
    // For debugging
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, 
      config.data ? { data: config.data } : '');
      
    return config;
  },
  (error) => {
    console.error('API Request Error:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // For debugging
    console.log(`API Response (${response.status}):`, response.data);
    
    // Additional debugging for payment-related responses
    if (response.config.url?.includes('/api/payments/') || 
        response.config.url?.includes('/api/subscriptions/')) {
      console.log(`PAYMENT Response (${response.status}):`, {
        success: response.data.success,
        message: response.data.message,
        orderId: response.data.orderId || null,
        // Include other non-sensitive fields as needed
      });
      
      // Log payment-related response details to localStorage for debugging
      try {
        const paymentDebugData = {
          timestamp: new Date().toISOString(),
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          status: response.status,
          success: response.data.success,
          message: response.data.message || null,
          orderId: response.data.orderId || null
        };
        
        // Get existing debug info
        const existingDebugJson = localStorage.getItem('speedmeet_payment_api_debug');
        let existingDebug = existingDebugJson ? JSON.parse(existingDebugJson) : { responses: [] };
        
        // Add new entry, keeping only the latest 10
        existingDebug.responses = [paymentDebugData, ...(existingDebug.responses || [])].slice(0, 10);
        
        localStorage.setItem('speedmeet_payment_api_debug', JSON.stringify(existingDebug));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    // Store data in cache if it's a GET request and there's an ETag
    if (response.config.method?.toLowerCase() === 'get' && response.headers.etag) {
      const url = response.config.url || '';
      const params = new URLSearchParams(response.config.params || {});
      
      // Create a cache key for this request
      const sortedParams = Array.from(params.entries())
        .filter(([key]) => key !== '_t') // Exclude timestamp
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      
      const cacheKey = `cached-data-${url}${sortedParams ? `?${sortedParams}` : ''}`;
      
      // Store the response data and ETag
      localStorage.setItem(cacheKey, JSON.stringify(response.data));
      localStorage.setItem(`etag-${cacheKey}`, response.headers.etag);
      
      // Also store with a simpler key as fallback
      localStorage.setItem(`cached-data-${url}`, JSON.stringify(response.data));
      localStorage.setItem(`etag-${url}`, response.headers.etag);
    }
    
    return response;
  },
  async (error) => {
    // Handle 304 Not Modified as a non-error condition
    if (error.response?.status === 304) {
      console.log(`API Response (304): Using cached data for ${error.config?.url}`);
      
      // Parse the URL to get the endpoint path
      const url = error.config?.url || '';
      
      // Create a uniquely identifiable cache key based on URL and query params
      const params = new URLSearchParams(error.config?.params || {});
      // Sort params to ensure consistent cache key
      const sortedParams = Array.from(params.entries())
        .filter(([key]) => key !== '_t') // Exclude timestamp
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      
      const cacheKey = `cached-data-${url}${sortedParams ? `?${sortedParams}` : ''}`;
      
      // Try to find cached data with the most precise key first
      let cachedData = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      
      // Fallback to a less specific key if no match
      if (!cachedData) {
        const fallbackKey = `cached-data-${url}`;
        cachedData = JSON.parse(localStorage.getItem(fallbackKey) || 'null');
      }
      
      if (cachedData) {
        // Return a successful response with cached data
        return {
          status: 200,
          statusText: 'OK (from cache)',
          data: cachedData,
          headers: error.response?.headers || {},
          config: error.config,
        };
      }
      
      // Even if we don't have cached data, we should not report this as an error
      console.log('No cached data found, but 304 is not an error');
      
      // Return empty success to prevent error
      return {
        status: 200,
        statusText: 'OK (no cache data)',
        data: { success: true, meetings: [], pagination: { total: 0, page: 1, pages: 1 } },
        headers: error.response?.headers || {},
        config: error.config,
      };
    }

    // Enhanced error logging and extraction
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      details: error.response?.data?.details
    };
    
    console.error('API Response Error:', errorDetails);
    
    // Log specific error information for debugging
    if (error.response?.data) {
      console.error('Error Response Data:', error.response.data);
      
      // If there are validation details, log them
      if (error.response.data.details) {
        console.error('Error Details:', error.response.data.details);
      }
      
      // If there's an error object, log it
      if (error.response.data.error) {
        console.error('Error Object:', error.response.data.error);
      }
    }
    
    // Special handling for payment-related error responses
    if (error.config?.url?.includes('/api/payments/') || 
        error.config?.url?.includes('/api/subscriptions/')) {
      console.error(`PAYMENT API Error (${error.response?.status || 'Network Error'}):`, {
        message: error.response?.data?.message || error.message,
        url: error.config.url,
        method: error.config.method?.toUpperCase(),
      });
      
      // Log payment-related error details to localStorage for debugging
      try {
        const paymentErrorData = {
          timestamp: new Date().toISOString(),
          url: error.config.url,
          method: error.config.method?.toUpperCase(),
          status: error.response?.status || 'Network Error',
          message: error.response?.data?.message || error.message,
          details: error.response?.data?.details || null,
          error: error.response?.data?.error || null
        };
        
        // Get existing debug info
        const existingDebugJson = localStorage.getItem('speedmeet_payment_api_debug');
        let existingDebug = existingDebugJson ? JSON.parse(existingDebugJson) : { responses: [], errors: [] };
        
        // Add new entry, keeping only the latest 10
        existingDebug.errors = [paymentErrorData, ...(existingDebug.errors || [])].slice(0, 10);
        
        localStorage.setItem('speedmeet_payment_api_debug', JSON.stringify(existingDebug));
      } catch (e) {
        // Ignore localStorage errors
      }
      
      // Retry payment-related requests once if they failed due to network issues
      if (!error.response && error.config && !error.config.__isRetryRequest && 
          error.config.isPaymentRequest) {
        console.log('Retrying payment request due to network error');
        error.config.__isRetryRequest = true;
        return apiClient(error.config);
      }
    }
    
    const originalRequest = error.config;
    
    // Handle 401 (Unauthorized) errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          console.log('No refresh token available, logging out');
          clearAuth();
          window.location.href = '/login'; // Redirect to login page
          return Promise.reject(error);
        }
        
        console.log('Attempting to refresh token');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
        });
        
        if (response.data.success && response.data.token) {
          const { token } = response.data;
          console.log('Token refreshed successfully');
          setToken(token);
          
          // Update the authorization header and retry
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        } else {
          throw new Error('Failed to refresh token');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        clearAuth();
        window.location.href = '/login'; // Redirect to login page
        return Promise.reject(refreshError);
      }
    }
    
    // Create a more detailed error object for components
    const enhancedError = {
      ...error,
      response: {
        ...error.response,
        data: {
          success: false,
          message: error.response?.data?.message || error.message || 'An error occurred',
          details: error.response?.data?.details || null,
          error: error.response?.data?.error || null,
          ...error.response?.data
        }
      }
    };
    
    return Promise.reject(enhancedError);
  }
);

export default apiClient;
